import { useEffect } from "react";
import { Form, Formik } from "formik";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/common";
import { passwordSchema } from "../utils";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth/hooks";

const DirtyTracker = ({ dirty, onDirtyChange }) => {
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  return null;
};

const PasswordSection = ({ onSubmit, toggles, onDirtyChange }) => {
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];

  return (
    <Card className="border-border/70 flex flex-col sm:min-h-[440px]">
      <CardHeader>
        <CardTitle className="text-primary">Password Update</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col flex-1">
        <Formik
          initialValues={{
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          }}
          validationSchema={passwordSchema}
          onSubmit={onSubmit}
        >
          {({ values, errors, touched, handleChange, isSubmitting, dirty }) => (
            <Form className="flex flex-col flex-1">
              <DirtyTracker dirty={dirty} onDirtyChange={onDirtyChange} />
              <div className="space-y-3">
                {[
                  {
                    key: "currentPassword",
                    label: "Current Password",
                    toggleKey: "current",
                  },
                  {
                    key: "newPassword",
                    label: "New Password",
                    toggleKey: "new",
                  },
                  {
                    key: "confirmPassword",
                    label: "Confirm Password",
                    toggleKey: "confirm",
                  },
                ].map(({ key, label, toggleKey }) => {
                  const shown =
                    toggles[
                      `show${toggleKey.charAt(0).toUpperCase() + toggleKey.slice(1)}`
                    ];
                  return (
                    <FormField
                      key={key}
                      label={label}
                      type={shown ? "text" : "password"}
                      name={key}
                      value={values[key]}
                      onChange={handleChange}
                      error={touched[key] && errors[key]}
                      required
                      rightIcon={
                        <button
                          type="button"
                          onClick={() => toggles.toggle(toggleKey)}
                        >
                          {shown ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      }
                    />
                  );
                })}
              </div>
              <div className="mt-auto flex justify-end pt-6">
                <Can
                  resource="user_profile"
                  action="update"
                  defaultView={true}
                  userPermissions={permissions}
                >
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    {isSubmitting ? "Saving..." : "Save Changes"}
                  </Button>
                </Can>
              </div>
            </Form>
          )}
        </Formik>
      </CardContent>
    </Card>
  );
};
export default PasswordSection;
